import React from 'react';

const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
const enabled = !!dsn;

let sentryPromise = null;
let sentryReady = false;
let pendingUser = null;

const toRate = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(parsed, 1));
};

const scrub = (value, depth = 0) => {
  if (depth > 5) return '[MaxDepth]';
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => scrub(item, depth + 1));
  return Object.entries(value).reduce((acc, [key, item]) => {
    const normalized = String(key).toLowerCase();
    if (
      normalized.includes('password') ||
      normalized.includes('token') ||
      normalized.includes('secret') ||
      normalized.includes('api_key') ||
      normalized === 'authorization' ||
      normalized === 'cookie'
    ) {
      acc[key] = '[Filtered]';
    } else {
      acc[key] = scrub(item, depth + 1);
    }
    return acc;
  }, {});
};

const loadSentry = () => {
  if (!enabled) return Promise.resolve(null);
  if (!sentryPromise) sentryPromise = import('@sentry/react');
  return sentryPromise;
};

const normalizedUser = (user) => {
  if (!user) return null;
  return {
    id: user.id ? String(user.id) : undefined,
    email: user.email || undefined,
    username: user.full_name || undefined,
  };
};

export const initSentry = () => {
  if (!enabled) return false;

  loadSentry().then((Sentry) => {
    const replaySessionRate = toRate(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0);
    const replayErrorRate = toRate(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 0);
    const integrations = [Sentry.browserTracingIntegration()];
    if (replaySessionRate > 0 || replayErrorRate > 0) {
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        })
      );
    }

    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      tracesSampleRate: toRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0),
      replaysSessionSampleRate: replaySessionRate,
      replaysOnErrorSampleRate: replayErrorRate,
      sendDefaultPii: String(process.env.REACT_APP_SENTRY_SEND_DEFAULT_PII || '').toLowerCase() === 'true',
      integrations,
      beforeSend(event) {
        if (event.request) {
          event.request.headers = scrub(event.request.headers || {});
          event.request.cookies = event.request.cookies ? '[Filtered]' : event.request.cookies;
          event.request.data = scrub(event.request.data);
        }
        event.extra = scrub(event.extra || {});
        event.contexts = scrub(event.contexts || {});
        return event;
      },
    });

    Sentry.setTag('service', 'frontend');
    sentryReady = true;
    if (pendingUser !== undefined) Sentry.setUser(normalizedUser(pendingUser));
  }).catch(() => {});

  return true;
};

export const installConsoleCapture = () => {
  if (!enabled || window.__mapCreatorSentryConsoleCapture) return;
  window.__mapCreatorSentryConsoleCapture = true;
  ['error', 'warn'].forEach((level) => {
    const original = console[level];
    console[level] = (...args) => {
      original.apply(console, args);
      loadSentry().then((Sentry) => {
        try {
          const first = args[0];
          if (first instanceof Error) {
            Sentry.captureException(first, { level });
          } else {
            Sentry.captureMessage(
              args.map((item) => (typeof item === 'string' ? item : JSON.stringify(scrub(item)))).join(' '),
              level
            );
          }
        } catch {
          // Never let logging interfere with the app.
        }
      });
    };
  });
};

export const captureApiError = (error) => {
  if (!enabled) return;
  const status = error?.response?.status;
  if (status && status < 500) return;
  loadSentry().then((Sentry) => {
    Sentry.withScope((scope) => {
      scope.setTag('kind', 'api_error');
      if (status) scope.setTag('http_status', String(status));
      if (error?.config?.method) scope.setTag('method', String(error.config.method).toUpperCase());
      if (error?.config?.url) scope.setContext('request', { url: error.config.url });
      Sentry.captureException(error);
    });
  });
};

export const captureFrontendException = (error, context = {}) => {
  if (!enabled) return;
  loadSentry().then((Sentry) => {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => scope.setContext(key, scrub(value)));
      Sentry.captureException(error);
    });
  });
};

export const setSentryUser = (user) => {
  pendingUser = user || null;
  if (!enabled || !sentryReady) return;
  loadSentry().then((Sentry) => Sentry.setUser(normalizedUser(user)));
};

export class SentryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    captureFrontendException(error, { react: { componentStack: errorInfo?.componentStack } });
  }

  render() {
    if (this.state.hasError) return this.props.fallback || null;
    return this.props.children;
  }
}
